import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useAuth } from "../../../auth/AuthContext";

import {
  logAuditEvent,
} from "../../../core/audit.service";

import {
  addItemToComanda,
  cancelEmptyComanda,
  closeComanda,
  decreaseComandaItem,
  finalizarSaidaInterna,
  increaseComandaItem,
  loadComandaItems,
  loadEstoqueProdutos,
  removeComandaItem,
  reopenComanda,
} from "../comandas.service";

import type {
  FormaPagamento,
  PagamentoComanda,
} from "../comandas.service";

import type {
  Comanda,
  ComandaItem,
  EstoqueProduto,
} from "../types";

import "./ComandaDetalhes.css";

type Props = {
  comanda: Comanda;

  onClose: () => void;

  onComandaChange: (
    comanda: Comanda,
  ) => void;

  onComandaClosed?: (
    comandaId: string,
  ) => void;

  onComandaReopened?: (
    comanda: Comanda,
  ) => void;
};

function formatBRL(
  value: number,
) {
  return new Intl.NumberFormat(
    "pt-BR",
    {
      style: "currency",
      currency: "BRL",
    },
  ).format(value);
}

function getErrorMessage(
  error: unknown,
) {
  if (
    typeof error === "object" &&
    error !== null &&
    "message" in error
  ) {
    return String(
      (
        error as {
          message?: string;
        }
      ).message ||
        "Não foi possível realizar a operação.",
    );
  }

  return "Não foi possível realizar a operação.";
}

export default function ComandaDetalhes({
  comanda,
  onClose,
  onComandaChange,
  onComandaClosed,
  onComandaReopened,
}: Props) {
  const {
    profile,
    session,
    can,
  } = useAuth();

  const comandaFechada =
    comanda.status === "fechada";

  const podeEditarComanda =
    !comandaFechada &&
    can("comandas.edit");

  const podeFecharComanda =
    !comandaFechada &&
    can("comandas.close");

  const podeReabrirComanda =
    comandaFechada &&
    can("comandas.reopen");
  const [
    produtos,
    setProdutos,
  ] =
    useState<EstoqueProduto[]>([]);

  const [
    items,
    setItems,
  ] =
    useState<ComandaItem[]>([]);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    processing,
    setProcessing,
  ] =
    useState(false);

  const [
    error,
    setError,
  ] =
    useState("");

  const [
    showPayment,
    setShowPayment,
  ] =
    useState(false);

  const [
    formaPagamento,
    setFormaPagamento,
  ] =
    useState<FormaPagamento>(
      "pix",
    );

  const [showReopen, setShowReopen] =
    useState(false);

  const [reopenReason, setReopenReason] =
    useState("");

  const [paymentMode, setPaymentMode] =
    useState<"unico" | "multiplo">("unico");

  const [paymentParts, setPaymentParts] =
    useState<PagamentoComanda[]>([
      { forma_pagamento: "pix", valor: 0 },
      { forma_pagamento: "dinheiro", valor: 0 },
    ]);

  const paymentPartsTotal = useMemo(
    () => paymentParts.reduce(
      (sum, part) => sum + Number(part.valor || 0),
      0,
    ),
    [paymentParts],
  );

  const refreshData =
    useCallback(
      async () => {
        const [
          productsResult,
          itemsResult,
        ] =
          await Promise.all([
            loadEstoqueProdutos(),

            loadComandaItems(
              comanda.id,
            ),
          ]);

        setProdutos(
          productsResult,
        );

        setItems(
          itemsResult,
        );
      },
      [comanda.id],
    );

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);

        await refreshData();

        setError("");
      } catch (err) {
        console.error(err);

        setError(
          "Não foi possível carregar a comanda.",
        );
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [refreshData]);

  const filteredProducts =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      if (!term) {
        return produtos;
      }

      return produtos.filter(
        (produto) =>
          produto.nome
            .toLowerCase()
            .includes(term),
      );
    }, [
      produtos,
      search,
    ]);

  const total =
    useMemo(
      () =>
        items.reduce(
          (
            sum,
            item,
          ) =>
            sum +
            Number(
              item.quantidade,
            ) *
              Number(
                item.valor_unitario,
              ),
          0,
        ),
      [items],
    );

  function updateParentTotal(
    newTotal: number,
  ) {
    onComandaChange({
      ...comanda,

      total:
        Number(
          newTotal,
        ),
    });
  }

  async function handleAdd(
    produto: EstoqueProduto,
  ) {
    if (!podeEditarComanda) {
      setError(
        "Você não possui permissão para alterar esta comanda.",
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result =
        await addItemToComanda(
          comanda.id,
          produto.id,
        );

      updateParentTotal(
        result.total,
      );

      await refreshData();

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ITEM_ADICIONADO_COMANDA",

          level:
            "success",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            `${profile?.nome || "Funcionário"} adicionou ${produto.nome} à comanda de ${comanda.cliente_nome}.`,

          metadata: {
            produto:
              produto.nome,

            quantidade:
              Number(
                result.item.quantidade,
              ),

            total_comanda:
              Number(
                result.total,
              ),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleIncrease(
    item: ComandaItem,
  ) {
    if (!podeEditarComanda) {
      setError(
        "Você não possui permissão para alterar esta comanda.",
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const before =
        Number(
          item.quantidade,
        );

      const result =
        await increaseComandaItem(
          item.id,
        );

      updateParentTotal(
        result.total,
      );

      await refreshData();

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ITEM_QUANTIDADE_ALTERADA",

          level:
            "info",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            `${profile?.nome || "Funcionário"} aumentou a quantidade de ${item.nome}.`,

          metadata: {
            produto:
              item.nome,

            antes:
              before,

            depois:
              Number(
                result.item.quantidade,
              ),

            total_comanda:
              Number(
                result.total,
              ),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleDecrease(
    item: ComandaItem,
  ) {
    if (!podeEditarComanda) {
      setError(
        "Você não possui permissão para alterar esta comanda.",
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const before =
        Number(
          item.quantidade,
        );

      const result =
        await decreaseComandaItem(
          item.id,
        );

      updateParentTotal(
        result.total,
      );

      await refreshData();

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            result.removed
              ? "ITEM_REMOVIDO_COMANDA"
              : "ITEM_QUANTIDADE_ALTERADA",

          level:
            result.removed
              ? "warning"
              : "info",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            result.removed
              ? `${profile?.nome || "Funcionário"} removeu ${item.nome} da comanda.`
              : `${profile?.nome || "Funcionário"} reduziu a quantidade de ${item.nome}.`,

          metadata: {
            produto:
              item.nome,

            antes:
              before,

            depois:
              result.removed
                ? 0
                : before - 1,

            total_comanda:
              Number(
                result.total,
              ),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleRemove(
    item: ComandaItem,
  ) {
    if (!podeEditarComanda) {
      setError(
        "Você não possui permissão para alterar esta comanda.",
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result =
        await removeComandaItem(
          item.id,
        );

      updateParentTotal(
        result.total,
      );

      await refreshData();

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "ITEM_REMOVIDO_COMANDA",

          level:
            "warning",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            `${profile?.nome || "Funcionário"} removeu ${item.nome} da comanda.`,

          metadata: {
            produto:
              item.nome,

            quantidade:
              Number(
                item.quantidade,
              ),

            total_comanda:
              Number(
                result.total,
              ),
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

    async function handleCancelComanda() {
    if (!podeFecharComanda) {
      setError(
        "Você não possui permissão para cancelar esta comanda.",
      );
      return;
    }

    if (
      items.length > 0 ||
      total !== 0
    ) {
      setError(
        "Remova todos os itens antes de cancelar a comanda.",
      );
      return;
    }

    const confirmed =
      window.confirm(
        `Deseja realmente cancelar a comanda de ${comanda.cliente_nome}?`,
      );

    if (!confirmed) {
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result =
        await cancelEmptyComanda(
          comanda.id,
        );

      onComandaChange(
        result.comanda,
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "COMANDA_CANCELADA",

          level:
            "warning",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            `${profile?.nome || "Funcionário"} cancelou a comanda vazia de ${comanda.cliente_nome}.`,

          metadata: {
            cliente:
              comanda.cliente_nome,

            motivo:
              "comanda_vazia",
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      onComandaClosed?.(
        comanda.id,
      );

      onClose();
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleCloseComanda() {
    if (!podeFecharComanda) {
      setError(
        "Você não possui permissão para fechar esta comanda.",
      );
      return;
    }

    if (
      total <= 0
    ) {
      setError(
        "Não é possível fechar uma comanda vazia.",
      );

      return;
    }

    const pagamentos: PagamentoComanda[] =
      paymentMode === "unico"
        ? [{ forma_pagamento: formaPagamento, valor: total }]
        : paymentParts;

    if (paymentMode === "multiplo" && Math.abs(paymentPartsTotal - total) > 0.009) {
      setError(`A soma dos pagamentos deve ser ${formatBRL(total)}.`);
      return;
    }

    if (pagamentos.some((item) => Number(item.valor) <= 0)) {
      setError("Todos os valores de pagamento devem ser maiores que zero.");
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result =
        await closeComanda(
          comanda.id,
          pagamentos,
        );

      const updated =
        result.comanda;

      onComandaChange(
        updated,
      );

      try {
        await logAuditEvent({
          userId:
            session?.user.id,

          employeeId:
            profile?.id,

          employeeName:
            profile?.nome,

          employeeLoginId:
            profile?.login_id,

          eventType:
            "COMANDA_FECHADA",

          level:
            "success",

          module:
            "comandas",

          entityType:
            "comanda",

          entityId:
            comanda.id,

          description:
            `${profile?.nome || "Funcionário"} fechou a comanda de ${comanda.cliente_nome}.`,

          metadata: {
            cliente:
              comanda.cliente_nome,

            total:
              Number(
                result.total,
              ),

            forma_pagamento:
              result.forma_pagamento,

            pagamentos: result.pagamentos,

            movimentos_caixa_ids:
              result.movimento_ids,
          },
        });
      } catch (
        auditError
      ) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      onComandaClosed?.(
        comanda.id,
      );

      onClose();
    } catch (err) {
      console.error(err);

      setError(
        getErrorMessage(
          err,
        ),
      );
    } finally {
      setProcessing(false);
    }
  }

  async function handleFinalizarSaidaInterna() {
    if (!podeFecharComanda) {
      setError(
        "Você não possui permissão para confirmar esta saída interna.",
      );
      return;
    }

    if (total <= 0 || items.length === 0) {
      setError(
        "Adicione pelo menos um item antes de confirmar a saída.",
      );
      return;
    }

    const confirmed = window.confirm(
      `Confirmar a retirada para ${comanda.cliente_nome}? Esta operação não criará entrada no Financeiro.`,
    );

    if (!confirmed) {
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result = await finalizarSaidaInterna(
        comanda.id,
      );

      onComandaChange(result.comanda);

      try {
        await logAuditEvent({
          userId: session?.user.id,
          employeeId: profile?.id,
          employeeName: profile?.nome,
          employeeLoginId: profile?.login_id,
          eventType: "SAIDA_INTERNA_CONFIRMADA",
          level: "warning",
          module: "comandas",
          entityType: "comanda",
          entityId: comanda.id,
          description:
            `${profile?.nome || "Funcionário"} confirmou uma saída interna para ${comanda.cliente_nome}.`,
          metadata: {
            responsavel: comanda.cliente_nome,
            motivo: comanda.motivo_saida,
            observacao: comanda.observacao_saida,
            valor_venda_referencia: Number(
              result.total_referencia,
            ),
            itens: items.map((item) => ({
              nome: item.nome,
              quantidade: Number(item.quantidade),
              valor_unitario: Number(item.valor_unitario),
            })),
            gera_movimento_caixa: false,
          },
        });
      } catch (auditError) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      onComandaClosed?.(comanda.id);
      onClose();
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  }

  async function handleReopenComanda() {
    const cleanReason = reopenReason.trim();

    if (!podeReabrirComanda) {
      setError(
        "Somente um administrador pode reabrir uma comanda.",
      );
      return;
    }

    if (cleanReason.length < 5) {
      setError(
        "Informe um motivo com pelo menos 5 caracteres.",
      );
      return;
    }

    try {
      setProcessing(true);
      setError("");

      const result = await reopenComanda(
        comanda.id,
        cleanReason,
      );

      try {
        await logAuditEvent({
          userId: session?.user.id,
          employeeId: profile?.id,
          employeeName: profile?.nome,
          employeeLoginId: profile?.login_id,
          eventType: "COMANDA_REABERTA",
          level: "warning",
          module: "comandas",
          entityType: "comanda",
          entityId: comanda.id,
          description:
            `${profile?.nome || "Administrador"} reabriu a comanda de ${comanda.cliente_nome}.`,
          metadata: {
            cliente: comanda.cliente_nome,
            motivo: cleanReason,
            valor_anterior: Number(
              result.valor_anterior,
            ),
            movimentos_arquivados:
              result.movimentos_arquivados,
          },
        });
      } catch (auditError) {
        console.error(
          "Falha na auditoria:",
          auditError,
        );
      }

      setShowReopen(false);
      setReopenReason("");
      onComandaReopened?.(result.comanda);
    } catch (err) {
      console.error(err);
      setError(getErrorMessage(err));
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="pdv-backdrop">
      <div
        className={`pdv ${
          comandaFechada
            ? "pdv-readonly"
            : ""
        }`}
      >
        <div className="pdv-header">
          <div>
            <span className="pdv-eyebrow">
              {comanda.tipo === "saida_interna"
                ? "SAÍDA INTERNA"
                : "COMANDA"}
            </span>

            <h2>
              {
                comanda.cliente_nome
              }
            </h2>

            <p>
              {comanda.tipo === "saida_interna"
                ? "Valor de venda de referência: "
                : "Total atual: "}
              <strong>
                {formatBRL(
                  total,
                )}
              </strong>
            </p>

            {comandaFechada && (
              <p className="pdv-closed-description">
                Fechada em {formatDateTime(comanda.closed_at)} · {formatPayment(comanda.forma_pagamento)}
              </p>
            )}

            {comanda.tipo === "saida_interna" && (
              <p className="pdv-internal-description">
                Motivo: {formatInternalReason(comanda.motivo_saida)}
                {comanda.observacao_saida
                  ? ` · ${comanda.observacao_saida}`
                  : ""}
              </p>
            )}
          </div>

          <button
            type="button"
            className="pdv-close"
            onClick={onClose}
            disabled={
              processing
            }
          >
            ×
          </button>
        </div>

        {error && (
          <div className="pdv-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="pdv-loading">
            Carregando comanda...
          </div>
        ) : (
          <div className="pdv-content">
            <section className="pdv-products">
              <div className="pdv-section-header">
                <div>
                  <span className="pdv-eyebrow">
                    PRODUTOS
                  </span>

                  <h3>
                    Adicionar item
                  </h3>
                </div>

                <input
                  type="search"
                  value={search}
                  onChange={(
                    event,
                  ) =>
                    setSearch(
                      event.target.value,
                    )
                  }
                  placeholder="Buscar produto..."
                />
              </div>

              <div className="pdv-product-grid">
                {filteredProducts.map(
                  (
                    produto,
                  ) => {
                    const semEstoque =
                      Number(
                        produto.quantidade,
                      ) <= 0;

                    return (
                      <button
                        key={
                          produto.id
                        }
                        type="button"
                        className="pdv-product"
                        onClick={() =>
                          handleAdd(
                            produto,
                          )
                        }
                        disabled={
                          processing ||
                          semEstoque ||
                          !podeEditarComanda
                        }
                      >
                        <div>
                          <strong>
                            {
                              produto.nome
                            }
                          </strong>

                          <span>
                            {semEstoque
                              ? "Sem estoque"
                              : `Estoque: ${produto.quantidade} ${produto.unidade}`}
                          </span>
                        </div>

                        <div className="pdv-product-price">
                          {formatBRL(
                            Number(
                              produto.valor,
                            ),
                          )}

                          <b>
                            {semEstoque
                              ? "×"
                              : "+"}
                          </b>
                        </div>
                      </button>
                    );
                  },
                )}
              </div>
            </section>

            <section className="pdv-cart">
              <div className="pdv-section-header">
                <div>
                  <span className="pdv-eyebrow">
                    ITENS
                  </span>

                  <h3>
                    {comanda.tipo === "saida_interna"
                      ? "Itens da retirada"
                      : "Comanda"}
                  </h3>
                </div>

                <span>
                  {
                    items.length
                  }{" "}
                  tipos de item
                </span>
              </div>

              {items.length ===
              0 ? (
                <div className="pdv-empty">
                  Nenhum produto adicionado.
                </div>
              ) : (
                <div className="pdv-cart-list">
                  {items.map(
                    (item) => {
                      const subtotal =
                        Number(
                          item.quantidade,
                        ) *
                        Number(
                          item.valor_unitario,
                        );

                      return (
                        <div
                          key={
                            item.id
                          }
                          className="pdv-cart-item"
                        >
                          <div className="pdv-cart-item-info">
                            <strong>
                              {
                                item.nome
                              }
                            </strong>

                            <span>
                              {formatBRL(
                                Number(
                                  item.valor_unitario,
                                ),
                              )}{" "}
                              cada
                            </span>
                          </div>

                          <div className="pdv-quantity">
                            <button
                              type="button"
                              onClick={() =>
                                handleDecrease(
                                  item,
                                )
                              }
                              disabled={
                                processing ||
                                !podeEditarComanda
                              }
                            >
                              −
                            </button>

                            <strong>
                              {
                                item.quantidade
                              }
                            </strong>

                            <button
                              type="button"
                              onClick={() =>
                                handleIncrease(
                                  item,
                                )
                              }
                              disabled={
                                processing ||
                                !podeEditarComanda
                              }
                            >
                              +
                            </button>
                          </div>

                          <strong className="pdv-subtotal">
                            {formatBRL(
                              subtotal,
                            )}
                          </strong>

                          <button
                            type="button"
                            className="pdv-remove"
                            onClick={() =>
                              handleRemove(
                                item,
                              )
                            }
                            disabled={
                              processing ||
                              !podeEditarComanda
                            }
                          >
                            ×
                          </button>
                        </div>
                      );
                    },
                  )}
                </div>
              )}

              <div className="pdv-total">
                <span>
                  {comanda.tipo === "saida_interna"
                    ? "VALOR DE REFERÊNCIA"
                    : "TOTAL"}
                </span>

                <strong>
                  {formatBRL(
                    total,
                  )}
                </strong>
              </div>

              {comandaFechada ? (
                <div className="pdv-closed-actions">
                  <div className="pdv-closed-notice">
                    Esta comanda está fechada e disponível somente para consulta.
                  </div>

                  {podeReabrirComanda && (
                    <button
                      type="button"
                      className="pdv-reopen"
                      onClick={() => {
                        setReopenReason("");
                        setError("");
                        setShowReopen(true);
                      }}
                      disabled={processing}
                    >
                      Reabrir para correção
                    </button>
                  )}
                </div>
              ) : comanda.tipo === "saida_interna" ? (
                <div className="pdv-primary-actions">
                  {items.length === 0 &&
                    total === 0 &&
                    podeFecharComanda && (
                      <button
                        type="button"
                        className="pdv-cancel-empty"
                        disabled={processing}
                        onClick={handleCancelComanda}
                      >
                        {processing
                          ? "Cancelando..."
                          : "Cancelar saída"}
                      </button>
                    )}

                  <button
                    type="button"
                    className="pdv-finish pdv-finish-internal"
                    disabled={
                      processing ||
                      total <= 0 ||
                      !podeFecharComanda
                    }
                    onClick={handleFinalizarSaidaInterna}
                  >
                    {processing
                      ? "Confirmando..."
                      : "Confirmar saída interna"}
                  </button>

                  <div className="pdv-internal-warning">
                    Esta retirada baixa o estoque e não registra recebimento no Financeiro.
                  </div>
                </div>
              ) : !showPayment ? (
                <div className="pdv-primary-actions">
  {items.length === 0 &&
    total === 0 &&
    podeFecharComanda && (
      <button
        type="button"
        className="pdv-cancel-empty"
        disabled={
          processing
        }
        onClick={
          handleCancelComanda
        }
      >
        {processing
          ? "Cancelando..."
          : "Cancelar comanda"}
      </button>
    )}

  <button
    type="button"
    className="pdv-finish"
    disabled={
      processing ||
      total <= 0 ||
      !podeFecharComanda
    }
    onClick={() =>
      setShowPayment(
        true,
      )
    }
  >
    Fechar comanda
  </button>
</div>
              ) : (
                <div className="pdv-payment">
                  <span className="pdv-eyebrow">
                    PAGAMENTO
                  </span>

                  <h3>
                    Como foi pago?
                  </h3>

                  <div className="pdv-payment-mode">
                    <button
                      type="button"
                      className={paymentMode === "unico" ? "active" : ""}
                      onClick={() => setPaymentMode("unico")}
                    >
                      Uma forma
                    </button>
                    <button
                      type="button"
                      className={paymentMode === "multiplo" ? "active" : ""}
                      onClick={() => {
                        setPaymentMode("multiplo");
                        setPaymentParts([
                          { forma_pagamento: "pix", valor: Number((total / 2).toFixed(2)) },
                          { forma_pagamento: "dinheiro", valor: Number((total - Number((total / 2).toFixed(2))).toFixed(2)) },
                        ]);
                      }}
                    >
                      Duas ou mais formas
                    </button>
                  </div>

                  {paymentMode === "unico" ? (
                  <div className="pdv-payment-options">
                    {[
                      {
                        id: "pix",
                        label: "Pix",
                      },

                      {
                        id: "dinheiro",
                        label: "Dinheiro",
                      },

                      {
                        id: "debito",
                        label: "Débito",
                      },

                      {
                        id: "credito",
                        label: "Crédito",
                      },
                    ].map(
                      (option) => (
                        <button
                          type="button"
                          key={
                            option.id
                          }
                          className={
                            formaPagamento ===
                            option.id
                              ? "active"
                              : ""
                          }
                          onClick={() =>
                            setFormaPagamento(
                              option.id as FormaPagamento,
                            )
                          }
                          disabled={
                            processing ||
                            !podeFecharComanda
                          }
                        >
                          {
                            option.label
                          }
                        </button>
                      ),
                    )}
                  </div>
                  ) : (
                    <div className="pdv-payment-parts">
                      {paymentParts.map((part, index) => (
                        <div className="pdv-payment-part" key={index}>
                          <select
                            value={part.forma_pagamento}
                            onChange={(event) =>
                              setPaymentParts((current) => current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, forma_pagamento: event.target.value as FormaPagamento }
                                  : item,
                              ))
                            }
                          >
                            <option value="pix">Pix</option>
                            <option value="dinheiro">Dinheiro</option>
                            <option value="debito">Débito</option>
                            <option value="credito">Crédito</option>
                          </select>
                          <input
                            type="number"
                            min="0.01"
                            step="0.01"
                            value={part.valor || ""}
                            onChange={(event) =>
                              setPaymentParts((current) => current.map((item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, valor: Number(event.target.value) }
                                  : item,
                              ))
                            }
                          />
                          {paymentParts.length > 2 && (
                            <button
                              type="button"
                              className="remove"
                              onClick={() => setPaymentParts((current) => current.filter((_, itemIndex) => itemIndex !== index))}
                            >×</button>
                          )}
                        </div>
                      ))}
                      <button
                        type="button"
                        className="pdv-add-payment-part"
                        onClick={() => setPaymentParts((current) => [
                          ...current,
                          { forma_pagamento: "pix", valor: 0 },
                        ])}
                      >
                        + Adicionar outra forma
                      </button>
                      <div className={Math.abs(paymentPartsTotal - total) <= 0.009 ? "pdv-payment-balance ok" : "pdv-payment-balance"}>
                        Soma: {formatBRL(paymentPartsTotal)} · Falta: {formatBRL(total - paymentPartsTotal)}
                      </div>
                    </div>
                  )}

                  <div className="pdv-payment-total">
                    <span>
                      Total a receber
                    </span>

                    <strong>
                      {formatBRL(
                        total,
                      )}
                    </strong>
                  </div>

                  <div className="pdv-payment-actions">
                    <button
                      type="button"
                      className="pdv-payment-back"
                      onClick={() =>
                        setShowPayment(
                          false,
                        )
                      }
                      disabled={
                        processing
                      }
                    >
                      Voltar
                    </button>

                    <button
                      type="button"
                      className="pdv-payment-confirm"
                      onClick={
                        handleCloseComanda
                      }
                      disabled={
                        processing ||
                        !podeFecharComanda ||
                        (paymentMode === "multiplo" && Math.abs(paymentPartsTotal - total) > 0.009)
                      }
                    >
                      {processing
                        ? "Finalizando..."
                        : "Confirmar pagamento"}
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        )}

        {showReopen && (
          <div
            className="pdv-reopen-backdrop"
            onClick={() =>
              !processing && setShowReopen(false)
            }
          >
            <div
              className="pdv-reopen-modal"
              onClick={(event) =>
                event.stopPropagation()
              }
            >
              <span className="pdv-eyebrow">
                CORREÇÃO FINANCEIRA
              </span>

              <h3>Reabrir comanda?</h3>

              <p>
                O fechamento anterior de {formatBRL(total)} será arquivado como substituído, sem criar saída no Financeiro. Depois da correção, a comanda deverá ser fechada novamente.
              </p>

              <label>
                Motivo obrigatório
                <textarea
                  autoFocus
                  value={reopenReason}
                  onChange={(event) =>
                    setReopenReason(event.target.value)
                  }
                  placeholder="Ex.: produto lançado incorretamente"
                  rows={4}
                  maxLength={300}
                />
              </label>

              {error && (
                <div className="pdv-error">
                  {error}
                </div>
              )}

              <div className="pdv-reopen-modal-actions">
                <button
                  type="button"
                  onClick={() => setShowReopen(false)}
                  disabled={processing}
                >
                  Cancelar
                </button>

                <button
                  type="button"
                  className="confirm"
                  onClick={handleReopenComanda}
                  disabled={
                    processing ||
                    reopenReason.trim().length < 5
                  }
                >
                  {processing
                    ? "Reabrindo..."
                    : "Confirmar reabertura"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function formatInternalReason(
  reason: string | null,
) {
  const labels: Record<string, string> = {
    consumo_interno: "Consumo interno",
    cortesia: "Cortesia",
    perda_avaria: "Perda ou avaria",
    vencimento: "Vencimento",
    uso_evento: "Uso em evento",
    outro: "Outro",
  };

  return reason
    ? labels[reason] || reason
    : "Não informado";
}

function formatDateTime(
  value: string | null,
) {
  if (!value) return "data não informada";

  return new Intl.DateTimeFormat(
    "pt-BR",
    {
      dateStyle: "short",
      timeStyle: "short",
    },
  ).format(new Date(value));
}

function formatPayment(
  value: string | null,
) {
  const labels: Record<string, string> = {
    pix: "Pix",
    dinheiro: "Dinheiro",
    debito: "Débito",
    credito: "Crédito",
    multiplo: "Pagamento dividido",
  };

  return value
    ? labels[value] || value
    : "forma de pagamento não informada";
}
